import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import PublicSessions from './pages/PublicSessions';
import PrivateSessions from './pages/PrivateSessions';
import Tutors from './pages/Tutors';
import TutorAddEdit from './components/Tutor/TutorAddEdit';
import TutorView from './components/Tutor/TutorView';
import TopicView from './components/Topic/TopicView';
import Topics from './pages/Topics';
import SessionAdd from './pages/SessionAdd';
import PublicMultipleSessions from './pages/PublicMultipleSessions';
// import Playground from './pages/Playground';
import Analytics from './pages/Analytics';
import Block from './pages/Block';
import { UserProvider, useUserContext } from './components/Providers/UserContext';
import { TimeZoneProvider } from './providers/TimeZoneProvider';
import { TutorProvider } from './providers/TutorContext';
import NavHeader from './components/layout/NavHeader';
import { SessionProvider } from 'providers/SessionContext';
import TopicAdd from './pages/TopicAdd';
import { TopicProvider } from 'providers/TopicContext';
import { PrivateGroupsProvider } from './providers/privateGroupsContext';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useFetchTopics from './Query/useFetchTopics';
import useFetchTutors from './Query/useFetchTutors';
import Welcome from './pages/Welcome';
import { ApiError } from './utils/Network';
import { Roles } from './utils/types';
import { useState } from 'react';

const AppBody = () => {
  const { pathname } = useLocation();
  const { userToken, user, hasRole, init, backToLogin } = useUserContext();

  const {
    data: allTopics = [],
    error: TopicsError,
    isPending: PendingTopics,
  } = useFetchTopics(userToken);

  if (TopicsError) {
    console.log('An error has occurred: ' + TopicsError.message);
  }

  const {
    data: tutorList = [],
    error: TutorsError,
    isPending: PendingTutors,
  } = useFetchTutors(userToken);

  if (TutorsError) {
    console.log('An error has occurred: ' + TutorsError.message);
  }

  const queryParams = new URLSearchParams(window.location.search);
  const noAzureLogin = queryParams.get('noAzure') && queryParams.get('noAzure') === '1';
  if (!noAzureLogin) {
    if (!init) return null;
    const pathNames = ['/', '/login'];
    if (!user && !pathNames.includes(pathname)) {
      return <Navigate replace to="/login" />;
    }

    if (!user) {
      return <Welcome />;
    }

    if (user && !hasRole(Roles.ADMIN)) {
      return (
        <div
          style={{
            width: 500,
            margin: '100px auto',
            textAlign: 'center',
            fontSize: 20,
          }}
        >
          You do not have permission to access the Tutoring Admin. Please go to MyAccess and request
          access
          <button
            onClick={backToLogin}
            style={{
              padding: '12px 32px',
              fontSize: 16,
              cursor: 'pointer',
              backgroundColor: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              margin: '16px auto 0',
              display: 'block',
            }}
          >
            Back To Login
          </button>
        </div>
      );
    }
  }

  return (
    <div className="page-container">
      <Routes>
        <Route path="/login" element={<Welcome />} />
        {user ? (
          <>
            {/* public */}
            <Route
              path="/"
              element={
                <div className="page-container">
                  <NavHeader />
                  <PublicSessions />
                </div>
              }
            />
            <Route
              path="/publicsessions"
              element={
                <div className="page-container">
                  <NavHeader />
                  <PublicSessions />
                </div>
              }
            />
            <Route
              path="/sessionadd"
              element={
                <div className="page-container">
                  <NavHeader />
                  <SessionAdd tutorList={tutorList} topicList={allTopics} />
                </div>
              }
            />
            <Route
              path="/multiplesessionaddpublic"
              element={
                <div className="page-container">
                  <NavHeader />
                  <PublicMultipleSessions />
                </div>
              }
            />
            {/* private */}
            <Route
              path="/privatesessions/:groupId/*"
              element={
                <div className="page-container">
                  <NavHeader />
                  <PrivateSessions tutorList={tutorList} topicList={allTopics} />
                </div>
              }
            />
            {/* tutor */}
            <Route
              path="/tutor"
              element={
                <div className="page-container">
                  <NavHeader />
                  <Tutors tutorList={tutorList} isLoading={PendingTutors} />
                </div>
              }
            />
            <Route
              path="/tutoradd"
              element={
                <div className="page-container">
                  <NavHeader />
                  <TutorAddEdit />
                </div>
              }
            />
            <Route
              path="/tutorview"
              element={
                <div className="page-container">
                  <NavHeader />
                  <TutorView />
                </div>
              }
            />
            {/* topic */}
            <Route
              path="/topics"
              element={
                <div className="page-container">
                  <NavHeader />
                  <Topics topicList={allTopics} isLoading={PendingTopics} />
                </div>
              }
            />
            <Route
              path="/topicview/:topicId"
              element={
                <div className="page-container">
                  <NavHeader />
                  <TopicView />
                </div>
              }
            />
            <Route
              path="/topicadd"
              element={
                <div className="page-container">
                  <NavHeader />
                  <TopicAdd />
                </div>
              }
            />
            {/* playground */}
            {/* <Route path="/playground" element={<Playground />} /> */}
            <Route
              path="/block"
              element={
                <div className="page-container">
                  <NavHeader />
                  <Block />
                </div>
              }
            />
            <Route
              path="/analytics"
              element={
                <div className="page-container">
                  <NavHeader />
                  <Analytics />
                </div>
              }
            />
          </>
        ) : (
          <Route path="*" element={<Navigate replace to="/login" />} />
        )}
      </Routes>
    </div>
  );
};

function App() {
  const navigate = useNavigate();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error: Error) => {
            if (error instanceof ApiError && error?.httpStatus === 401) {
              console.info('Session expired, redirecting to login...');
              sessionStorage.removeItem('session_token');
              sessionStorage.removeItem('user_info');
              sessionStorage.removeItem('authType');
              navigate('/login');
            }
          },
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            retry: (failureCount, error) => {
              if (error instanceof ApiError && error.httpStatus === 401) {
                return false;
              }
              return failureCount > 3;
            },
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider>
        <TutorProvider>
          <SessionProvider>
            <TopicProvider>
              <TimeZoneProvider>
                <PrivateGroupsProvider>
                  <AppBody />
                </PrivateGroupsProvider>
              </TimeZoneProvider>
            </TopicProvider>
          </SessionProvider>
        </TutorProvider>
      </UserProvider>
    </QueryClientProvider>
  );
}

export default App;
